import * as React from 'react';
import Paper from '@material-ui/core/Paper';
import {
    EditingState, RowDetailState, SearchState, IntegratedFiltering, FilteringState,
    SortingState, IntegratedSorting, DataTypeProvider
} from '@devexpress/dx-react-grid';
import {
    Grid,
    Table,
    Toolbar,
    SearchPanel,
    TableHeaderRow,
    TableEditRow,
    TableEditColumn,
    TableRowDetail
} from '@devexpress/dx-react-grid-material-ui';

import {Query} from 'react-apollo'
import {
    GET_INSTRUMENTS_AND_HEADERS_OVERVIEW,
    GET_INSTRUMENT_NOPS_CHECK,
    GET_INSTRUMENT_RQ_CHECK,
    GET_INSTRUMENT_DEPLOYMENTS,
    GET_INSTRUMENT_HARDWARESTATUS,
    GET_INSTRUMENT_ISSUES,
    GET_INSTRUMENT_STATI
} from '../../apollo/queries/instruments'
import DeploymentTable from './DeploymentTable'
import StatusTable from './StatusTable'
import IssueTable from './IssueTable'
import {withStyles} from '@material-ui/core/styles';
import StatusTimeline from '../../components/timelines/Status'
import LiveStatusTimeline from '../../components/timelines/LiveStatus'
import IssueTimeline from '../../components/timelines/Issue'
import LiveStatusDetail from './utils/LiveStatusDetail'
import HardwareTable from './HardwareTable'
import InstrumentSummary from './InstrumentSummary'
import {STATUS_SORTING, getCheckColor} from '../../constants'
import FilterPGN from './utils/FilterPGN'
import {NopsCheckDetail, RoutineQcCheckDetail} from './utils/QualityCheckDetail'


const InstrumentCheckDetail = ({row, query, detailComponent: Detail}) => (
    <Query query={query} variables={{input: row.id}} pollInterval={180000}>
        {({loading, error, data}) => {
            if (loading) return <p>Loading quality check...</p>
            if (error) return <p>Error loading quality check</p>
            const instrument = data && data.instrument
            return <Detail data={{...row, ...instrument}}/>
        }}
    </Query>
)

const getRowId = row => row.id

const styles = theme => ({
    selected: {
        //backgroundColor: theme.palette.background.paper,
        backgroundColor: 'whitesmoke',
    },
});

const SQUARE_SIZE = 20
const LIVE_STATUS_REFRESH_INTERVAL = 180000

const getCheckTitle = (dateStr, checkType) => {
    const label = checkType === 'nopsCheck' ? 'NOps' : 'Routine QC'
    return `${label}: ${dateStr || 'None'}`
}

const makeCheckSquare = (dateStr, checkType) => (
    <span
        title={getCheckTitle(dateStr, checkType)}
        style={{
            display: 'inline-block',
            width: `${SQUARE_SIZE}px`,
            height: `${SQUARE_SIZE}px`,
            borderRadius: '2px',
            marginRight: '3px',
            backgroundColor: getCheckColor(dateStr, checkType)
        }}
    />
)

const NopsCheckFormatter = ({value}) => (
    <span style={{display: 'flex', alignItems: 'center'}}>
        {makeCheckSquare(value, 'nopsCheck')}
    </span>
)

const RoutineQcCheckFormatter = ({value}) => (
    <span style={{display: 'flex', alignItems: 'center'}}>
        {makeCheckSquare(value, 'rqCheck')}
    </span>
)

const NopsCheckTypeProvider = props => (
    <DataTypeProvider
        formatterComponent={NopsCheckFormatter}
        {...props}
    />
)

const RoutineQcCheckTypeProvider = props => (
    <DataTypeProvider
        formatterComponent={RoutineQcCheckFormatter}
        {...props}
    />
)

const StatusFormatter = ({row}) => (<StatusTimeline data={row.stati} size={[SQUARE_SIZE, SQUARE_SIZE]}/>)

const StatusTypeProvider = props => (
    <DataTypeProvider
        formatterComponent={StatusFormatter}
        {...props}
    />
)

const LiveStatusFormatter = ({row}) => (
    <LiveStatusTimeline
        data={row.partialHeaders}
        l2Files={row.l2Files}
        locationMatch={row.headerLocationNotMatching}
        size={[SQUARE_SIZE * 2 + 3, SQUARE_SIZE]}
        squareSize={SQUARE_SIZE}
    />
)

const LiveStatusTypeProvider = props => (
    <DataTypeProvider
        formatterComponent={LiveStatusFormatter}
        {...props}
    />
)


const dateVal = v => {
    if (!v || v === 'None') return -Infinity
    const t = new Date(v).getTime()
    return isNaN(t) ? -Infinity : t
}

const sortingExtensions = [
    {
        columnName: "currentStatus",
        compare: (a, b) => (STATUS_SORTING[a] - STATUS_SORTING[b])
    },
    {
        columnName: "nopsCheck",
        compare: (a, b) => dateVal(a) - dateVal(b)
    },
    {
        columnName: "rqCheck",
        compare: (a, b) => dateVal(a) - dateVal(b)
    }
]

const getHeaderLocation = headerData => {
    if (!headerData) {
        return null;
    }

    try {
        return JSON.parse(headerData)["LOCATION"];
    } catch (e) {
        return null;
    }
}

const formatUtcDate = date => {
    const year = date.getUTCFullYear()
    const month = String(date.getUTCMonth() + 1).padStart(2, '0')
    const day = String(date.getUTCDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

const getLiveStatusQueryVariables = () => {
    const now = new Date(
        Math.floor(Date.now() / LIVE_STATUS_REFRESH_INTERVAL) * LIVE_STATUS_REFRESH_INTERVAL
    )
    const thirtyMinutesAgo = new Date(now.getTime() - 30 * 60 * 1000)
    const yesterday = new Date(now.getTime())
    yesterday.setUTCDate(now.getUTCDate() - 1)

    return {
        start: thirtyMinutesAgo.toISOString(),
        end: now.toISOString(),
        l2Start: formatUtcDate(yesterday),
        l2End: formatUtcDate(now)
    }
}

const normalizePanId = panId => {
    if (panId === null || panId === undefined) {
        return ''
    }
    const match = String(panId).match(/\d+/)
    return match ? String(parseInt(match[0], 10)) : String(panId)
}


const stylesTable = theme => ({
    tableStriped: {
        '& tbody > tr:nth-of-type(odd)': {
            backgroundColor: '#f4f8ff',
        },
    },
});

const TableComponentBase = ({classes, ...restProps}) => (
    <Table.Table
        {...restProps}
        className={classes.tableStriped}
    />
);

const TableComponent = withStyles(stylesTable, {name: 'TableComponent'})(TableComponentBase);

const tableColumnExtensions = [
    {columnName: 'panId', width: 90},
    {columnName: 'currentStatus', width: 130},
    {columnName: 'liveStatus', width: 130},
    {columnName: 'nopsCheck', width: 80},
    {columnName: 'rqCheck', width: 80},
]


class EditableTable extends React.PureComponent {

    state = {
        expandedRow: undefined,
        expandedColumn: undefined,
        filters: []
    }

    handleCellClick = (column, rowId) => {
        if ((column === "hardwarestatus" && this.props.user.hweditor) || column !== "hardwarestatus") {
            return () => {
                return column !== this.state.expandedColumn || rowId !== this.state.expandedRow ?
                    this.setState({expandedRow: rowId, expandedColumn: column})
                    : this.setState({expandedRow: undefined, expandedColumn: undefined})
            }
        } else {
            return () => undefined;
        }
    }

    Cell = (props) => {
        const {classes, ...other} = props
        const {column, row, value} = other;

        return (<Table.Cell {...other} onClick={this.handleCellClick(column.name, row.id)}
                            style={{cursor: "pointer", paddingTop: "0px", paddingBottom: "0px", borderBottom: "0px"}}
                            className={this.state.expandedColumn === column.name && this.state.expandedRow === row.id ? classes.selected : ""}/>
        );
    }

    StyledCell = withStyles(styles)(this.Cell)

    RowDetail = ({row}) => {
        switch (this.state.expandedColumn) {
            case "panId":
                return <InstrumentSummary data={row} user={this.props.user}/>
            case "isAt":
                return (
                    <Query query={GET_INSTRUMENT_DEPLOYMENTS} variables={{input: row.id}} pollInterval={180000}>
                        {({loading, error, data}) => {
                            if (loading) {
                                return <p>Loading deployments...</p>
                            }
                            if (error) {
                                console.error("GET_INSTRUMENT_DEPLOYMENTS, error from instrument: ", error.message)
                                return <p>Error loading deployments</p>
                            }

                            const deployments = data && data.instrument ? data.instrument.deployment : []
                            return <DeploymentTable data={deployments} instrumentID={row.id} user={this.props.user}/>
                        }}
                    </Query>
                )
            case "currentStatus":
                return (
                    <Query query={GET_INSTRUMENT_STATI} variables={{input: row.id}} pollInterval={180000}>
                        {({loading, error, data}) => {
                            if (loading) {
                                return <p>Loading status assignments...</p>
                            }
                            if (error) {
                                console.error("GET_INSTRUMENT_STATI, error from instrument: ", error.message)
                                return <p>Error loading status assignments</p>
                            }

                            const stati = data && data.instrument ? data.instrument.stati : []
                            return <StatusTable data={stati} instrumentID={row.id} user={this.props.user}/>
                        }}
                    </Query>
                )
            case "nopsCheck":
                return <InstrumentCheckDetail row={row} query={GET_INSTRUMENT_NOPS_CHECK} detailComponent={NopsCheckDetail}/>
            case "rqCheck":
                return <InstrumentCheckDetail row={row} query={GET_INSTRUMENT_RQ_CHECK} detailComponent={RoutineQcCheckDetail}/>
            case "openIssues":
                return (
                    <Query query={GET_INSTRUMENT_ISSUES} variables={{input: row.id}} pollInterval={180000}>
                        {({loading, error, data}) => {
                            if (loading) {
                                return <p>Loading issues...</p>
                            }
                            if (error) {
                                console.error("GET_INSTRUMENT_ISSUES, error from instrument: ", error.message)
                                return <p>Error loading issues</p>
                            }

                            const issues = data && data.instrument ? data.instrument.issues : []
                            return <IssueTable data={issues} instrumentID={row.id} user={this.props.user}/>
                        }}
                    </Query>
                )
            case "liveStatus":
                return <LiveStatusDetail data={row.partialHeaders} l2Files={row.l2Files}/>
            case "hardwarestatus":
                return (
                    <Query query={GET_INSTRUMENT_HARDWARESTATUS} variables={{input: row.id}} pollInterval={180000}>
                        {({loading, error, data}) => {
                            if (loading) {
                                return <p>Loading hardware status...</p>
                            }
                            if (error) {
                                console.error("GET_INSTRUMENT_HARDWARESTATUS, error from instrument: ", error.message)
                                return <p>Error loading hardware status</p>
                            }

                            const hardwarestatus = data && data.instrument ? data.instrument.hardwarestatus : []
                            return <HardwareTable data={hardwarestatus} instrumentID={row.id} user={this.props.user}/>
                        }}
                    </Query>
                )
            default:
                return <p>nothing to see here</p>
        }
    }

    ContainerComponent = props => (<Table.Container {...props} style={{overflow: "visible"}}/>)
    ToggleComponent = props => (<Table.StubCell {...props} />)//toggleCellComponent={(props)=>(<Table.StubCell {...props} />)}

    render() {
        let data = this.props.data
        return (
            <Paper>
                <Grid
                    rows={data}
                    columns={this.props.columns}
                    getRowId={getRowId}
                    style={{overflow: "hidden"}}
                >
                    <StatusTypeProvider for={['currentStatus']}/>
                    <NopsCheckTypeProvider for={['nopsCheck']}/>
                    <RoutineQcCheckTypeProvider for={['rqCheck']}/>
                    <LiveStatusTypeProvider for={['liveStatus']}/>
                    <SortingState
                        defaultSorting={[{columnName: 'panId', direction: 'asc'}]}
                    />
                    <IntegratedSorting columnExtensions={sortingExtensions}/>
                    <SearchState defaultValue=""/>
                    <FilteringState
                        filters={this.state.filters}
                        //onFiltersChange={setFilters}
                    />
                    <IntegratedFiltering/>
                    <RowDetailState
                        expandedRowIds={this.state.expandedRow ? [this.state.expandedRow] : []}
                    />
                    <Table tableComponent={TableComponent} cellComponent={this.StyledCell}
                           columnExtensions={tableColumnExtensions}
                           containerComponent={this.ContainerComponent}
                    />
                    <TableHeaderRow showSortingControls/>
                    <Toolbar/>
                    <FilterPGN filtered={this.state.filters.length > 0}
                               toggleFilter={() => this.state.filters.length > 0 ? this.setState({filters: []}) : this.setState({
                                   filters: [{
                                       columnName: 'pgn',
                                       value: true
                                   }]
                               })}/>
                    <SearchPanel/>
                    <TableRowDetail
                        contentComponent={this.RowDetail}
                        toggleCellComponent={this.ToggleComponent}
                        toggleColumnWidth={0}
                    />
                </Grid>
            </Paper>
        );
    }
}

const columns = [{
    name: 'panId',
    title: 'Pan ID'
},
    {
        name: 'isAt',
        title: 'Location',
        getCellValue: row => (row.deployment.length > 0 ? row.deployment[0].location.name : 'None')
    },
    {
        name: 'currentStatus',
        title: 'Status',
        getCellValue: row => (row.stati.length > 0 ? row.stati[0].status.name : 'None')
    },
    {
        name: 'liveStatus',
        title: 'Live Status',
        getCellValue: row => (row.partialHeaders ? row.partialHeaders : 'None')
    },
    {
        name: 'nopsCheck',
        title: 'NOps',
        getCellValue: row => (row.currentNopsCheck && row.currentNopsCheck.checkDate) || 'None'
    },
    {
        name: 'rqCheck',
        title: 'RQC',
        getCellValue: row => (row.currentRqCheck && row.currentRqCheck.lastChecked) || 'None'
    },
    {
        name: 'openIssues',
        title: 'Issues',
        //getCellValue: row => (row.issues.length > 0 ? row.issues[0].issue.description : 'None')
        getCellValue: row => (<IssueTimeline data={row.issues} size={[90, 30]}/>)
    },
    {
        name: 'hardwarestatus',
        title: 'Hardware',
        getCellValue: row => (row.hardwarestatus.length > 0 ? row.hardwarestatus[0].displayName : 'None')
    },
    {
        name: 'blickoVersion',
        title: 'Software',
        getCellValue: row => (row.blickoVersion || '')
    },
    {
        name: 'networkOperator',
        title: 'Operator',
        getCellValue: row => (row.networkOperator && row.networkOperator.username)
    }]

const InstrumentView = (props) => {
    const [queryVariables, setQueryVariables] = React.useState(getLiveStatusQueryVariables)

    React.useEffect(() => {
        const refreshQueryVariables = () => setQueryVariables(getLiveStatusQueryVariables())
        const interval = window.setInterval(refreshQueryVariables, LIVE_STATUS_REFRESH_INTERVAL)
        const refreshVisibleTab = () => {
            if (!document.hidden) {
                refreshQueryVariables()
            }
        }

        document.addEventListener('visibilitychange', refreshVisibleTab)

        return () => {
            window.clearInterval(interval)
            document.removeEventListener('visibilitychange', refreshVisibleTab)
        }
    }, [])

    return (
        <Query
            query={GET_INSTRUMENTS_AND_HEADERS_OVERVIEW}
            variables={queryVariables}
            fetchPolicy="no-cache"
        >
            {({loading, error, data}) => {

                if (loading && !data) return <p>Loading...</p>;
                if (error) {
                    console.error("GET_INSTRUMENTS, error from instruments: ", error.message);
                    return <p>Error from Instuments</p>;
                }

                const l2FilesByPanId = (data.l2Files || []).reduce((acc, file) => {
                    const panId = normalizePanId(file.metadataPanId)
                    acc[panId] = acc[panId] || []
                    acc[panId].push(file)
                    return acc
                }, {})
                const partialHeaderStart = new Date(queryVariables.start).getTime()
                const partialHeaderEnd = new Date(queryVariables.end).getTime()

                const instruments = data.instruments.map(instrument => {
                    const hardwarestatus = instrument.currentHardwareStatus ? [instrument.currentHardwareStatus] : []
                    const deployment = instrument.currentDeployment ? [instrument.currentDeployment] : []
                    const stati = instrument.currentStatusAssignment ? [instrument.currentStatusAssignment] : []
                    const location = deployment[0] ? deployment[0].location : null
                    const partialHeaders = (instrument.partialHeaders || []).filter(header => {
                        const headerTime = new Date(header.time).getTime()
                        return headerTime >= partialHeaderStart && headerTime <= partialHeaderEnd
                    })
                    const headerData = partialHeaders[0] ? partialHeaders[0].data : null
                    const headerLocation = getHeaderLocation(headerData)
                    const headerLocationNotMatching = !headerLocation || !location || location.name !== headerLocation
                    const l2Files = l2FilesByPanId[normalizePanId(instrument.panId)] || []

                    return {
                        ...instrument,
                        hardwarestatus,
                        deployment,
                        stati,
                        partialHeaders,
                        l2Files,
                        headerLocationNotMatching
                    }
                })

                return (
                    <EditableTable columns={columns}
                                   data={instruments} user={props.user}
                    />
                );

            }}
        </Query>
    )

}


export default InstrumentView
